import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMessagesPlaceholders } from "stores";

const getPlaceholderLabelLength = (placeholders, key) => {
  if (!placeholders) {
    return 0;
  }
  const placeholder = placeholders.find((p) =>
    p.value.toLowerCase().includes(key)
  );
  return placeholder ? placeholder.label.length + 4 : 0;
};

export const useSmsLength = ({
  value,
  messageType,
  recipientOptions = [],
  maxLength,
}) => {
  const { t } = useTranslation();
  const { placeholders } = useMessagesPlaceholders();
  const [error, setError] = useState();
  const [length, setLength] = useState(0);

  const largestNames = useMemo(() => {
    let largestFirst = "";
    let largestLast = "";
    let largestFull = {
      firstName: "",
      lastName: "",
    };
    recipientOptions.forEach((option) => {
      if (option.firstName.length > largestFirst.length) {
        largestFirst = option.firstName;
      }
      if (option.lastName.length > largestLast.length) {
        largestLast = option.lastName;
      }
      const fullNameLength = option.firstName.length + option.lastName.length;
      if (
        fullNameLength >
        largestFull.firstName.length + largestFull.lastName.length
      ) {
        largestFull = {
          firstName: option.firstName,
          lastName: option.lastName,
        };
      }
    });
    return {
      firstName: largestFirst,
      lastName: largestLast,
      fullName: largestFull,
    };
  }, [recipientOptions]);

  const namePlaceholderLengths = useMemo(
    () => ({
      firstName: getPlaceholderLabelLength(placeholders, "firstname"),
      lastName: getPlaceholderLabelLength(placeholders, "lastname"),
    }),
    [placeholders]
  );

  const findMessageLength = useCallback(
    (message) => {
      if (!message || typeof message !== "string") {
        return 0;
      }

      const placeholderRegex = /{{(.*?)}}/g;
      const matches = message.match(placeholderRegex) || [];

      const actualMatches = [];
      matches.forEach((match) => {
        const label = match.replace(/{{|}}/g, "");
        const foundPlaceholder = placeholders.find(
          (placeholder) => placeholder.label === label
        );
        if (foundPlaceholder) {
          actualMatches.push(foundPlaceholder);
        }
      });

      if (!actualMatches.length) {
        return message.length;
      }

      const firstNamesMentioned = actualMatches.filter((match) =>
        match.value.toLowerCase().includes("firstname")
      ).length;
      const lastNamesMentioned = actualMatches.filter((match) =>
        match.value.toLowerCase().includes("lastname")
      ).length;

      const shouldConsiderBothNames = firstNamesMentioned && lastNamesMentioned;
      let names = {};
      if (shouldConsiderBothNames) {
        names = largestNames.fullName;
      } else {
        names = {
          firstName: largestNames.firstName,
          lastName: largestNames.lastName,
        };
      }

      const firstNameChars = firstNamesMentioned * names.firstName.length;
      const lastNameChars = lastNamesMentioned * names.lastName.length;

      const firstNamePlaceholderChars =
        firstNamesMentioned * namePlaceholderLengths.firstName;
      const lastNamePlaceholderChars =
        lastNamesMentioned * namePlaceholderLengths.lastName;

      return (
        message.length +
        firstNameChars +
        lastNameChars -
        firstNamePlaceholderChars -
        lastNamePlaceholderChars
      );
    },
    [placeholders, largestNames, namePlaceholderLengths]
  );

  const currentMessage = useMemo(() => {
    if (!(messageType === "sms" && value)) {
      return;
    }
    const editorContent = value.getCurrentContent();
    let messageText = editorContent.getPlainText();
    return messageText;
  }, [value, messageType]);

  useEffect(() => {
    if (typeof currentMessage !== "string") {
      return;
    }

    const messageLength = findMessageLength(currentMessage);
    setLength(messageLength);

    if (messageLength > maxLength) {
      setError(t("NewMessageModal.Message.ErrorTooLong"));
    } else {
      setError(null);
    }
  }, [currentMessage, findMessageLength, maxLength, t]);

  return { error, length };
};
